/**
 * Minimální čtečka ZIPu s náhodným přístupem pro export Google Takeout v prohlížeči.
 *
 * Čte VÝHRADNĚ přes `blob.slice(a, b).arrayBuffer()`:
 * centrální adresář jedním výřezem a potom jen položky, které volající chce. Na celý
 * soubor se nikdy nevolá `arrayBuffer()`, `text()` ani `stream()` — export s tisíci fotek
 * má gigabajty a čtou se z něj desítky kB (AK4). Z `File` se používá jen `size` a `slice`.
 *
 * Podporuje EOCD s komentářem, ZIP64 (lokátor, koncový záznam, rozšířené pole `0x0001`),
 * metody 0 a 8 a položky s datovým deskriptorem (velikosti vždy z centrálního adresáře).
 * CRC-32 se neověřuje: poškozený deflate zachytí dekomprese a kontrola délky.
 *
 * Načítá ho i CLI přes `nactiTs` (Node jen odstraňuje typy): jen vymazatelná syntaxe TS.
 */

/** Strop nekomprimované velikosti čtené položky (stránka recenzí má desítky kB). */
export const TAKEOUT_MAX_ENTRY_BYTES = 64 * 1024 * 1024;

export type ZipReadErrorKind = "not_zip" | "unsupported_zip";

/** Obsah souboru není čitelný ZIP (`not_zip`), nebo používá nepodporovanou vlastnost. */
export class ZipReadError extends Error {
  readonly kind: ZipReadErrorKind;
  constructor(kind: ZipReadErrorKind, detail: string) {
    super(`${kind}: ${detail}`);
    this.name = "ZipReadError";
    this.kind = kind;
  }
}

export interface ZipEntry {
  /** Jméno položky dekódované jako UTF-8 (bez ohledu na bit 11). */
  name: string;
  flags: number;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

const SIG_EOCD = 0x06054b50;
const SIG_ZIP64_LOCATOR = 0x07064b50;
const SIG_ZIP64_EOCD = 0x06064b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_LOCAL = 0x04034b50;
const EOCD_SIZE = 22;
const MAX_COMMENT = 0xffff;
const MAX16 = 0xffff;
const MAX32 = 0xffffffff;

const notZip = (detail: string) => new ZipReadError("not_zip", detail);
const unsupported = (detail: string) => new ZipReadError("unsupported_zip", detail);

async function readRange(blob: Blob, start: number, end: number): Promise<DataView> {
  if (start < 0 || end > blob.size || start > end) throw notZip("rozsah mimo soubor");
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function big(view: DataView, at: number): number {
  const v = view.getBigUint64(at, true);
  if (v > BigInt(Number.MAX_SAFE_INTEGER)) throw notZip("hodnota ZIP64 mimo rozsah");
  return Number(v);
}

interface Directory {
  count: number;
  size: number;
  offset: number;
}

/** Najde EOCD (odzadu, délka komentáře musí sedět se zbytkem) a případně záznamy ZIP64. */
async function findDirectory(blob: Blob): Promise<Directory> {
  if (blob.size < EOCD_SIZE) throw notZip("kratší než EOCD");
  const tailStart = Math.max(0, blob.size - (EOCD_SIZE + MAX_COMMENT));
  const tail = await readRange(blob, tailStart, blob.size);
  let at = -1;
  for (let i = tail.byteLength - EOCD_SIZE; i >= 0; i--) {
    if (
      tail.getUint32(i, true) === SIG_EOCD &&
      i + EOCD_SIZE + tail.getUint16(i + 20, true) === tail.byteLength
    ) {
      at = i;
      break;
    }
  }
  if (at < 0) throw notZip("chybí konec centrálního adresáře");
  if (tail.getUint16(at + 4, true) !== 0 || tail.getUint16(at + 6, true) !== 0) {
    throw unsupported("archiv na víc discích");
  }
  const dir: Directory = {
    count: tail.getUint16(at + 10, true),
    size: tail.getUint32(at + 12, true),
    offset: tail.getUint32(at + 16, true),
  };
  if (dir.count !== MAX16 && dir.size !== MAX32 && dir.offset !== MAX32) return dir;

  const eocdAt = tailStart + at;
  const locator = await readRange(blob, eocdAt - 20, eocdAt);
  if (locator.getUint32(0, true) !== SIG_ZIP64_LOCATOR) throw notZip("chybí lokátor ZIP64");
  if (locator.getUint32(4, true) !== 0) throw unsupported("archiv na víc discích");
  const recordAt = big(locator, 8);
  const record = await readRange(blob, recordAt, recordAt + 56);
  if (record.getUint32(0, true) !== SIG_ZIP64_EOCD) throw notZip("chybí koncový záznam ZIP64");
  if (record.getUint32(16, true) !== 0 || record.getUint32(20, true) !== 0) {
    throw unsupported("archiv na víc discích");
  }
  return { count: big(record, 32), size: big(record, 40), offset: big(record, 48) };
}

/**
 * Hodnoty z rozšířeného pole ZIP64 `0x0001`: jen ty, které jsou v pevné části `0xFFFFFFFF`,
 * v pevném pořadí nekomprimovaná, komprimovaná velikost, offset.
 */
function applyZip64Extra(view: DataView, start: number, length: number, entry: ZipEntry): void {
  const wants = [
    entry.uncompressedSize === MAX32,
    entry.compressedSize === MAX32,
    entry.localHeaderOffset === MAX32,
  ];
  if (!wants.some(Boolean)) return;
  for (let at = start; at + 4 <= start + length; ) {
    const id = view.getUint16(at, true);
    const size = view.getUint16(at + 2, true);
    if (at + 4 + size > start + length) throw notZip("rozšířené pole přesahuje záznam");
    if (id === 0x0001) {
      let p = at + 4;
      const next = (): number => {
        if (p + 8 > at + 4 + size) throw notZip("krátké rozšířené pole ZIP64");
        const v = big(view, p);
        p += 8;
        return v;
      };
      if (wants[0]) entry.uncompressedSize = next();
      if (wants[1]) entry.compressedSize = next();
      if (wants[2]) entry.localHeaderOffset = next();
      return;
    }
    at += 4 + size;
  }
  throw notZip("chybí rozšířené pole ZIP64");
}

/** Položky centrálního adresáře (bez adresářů). Chyby obsahu jako `ZipReadError`. */
export async function readZipDirectory(blob: Blob): Promise<ZipEntry[]> {
  const dir = await findDirectory(blob);
  if (dir.offset + dir.size > blob.size) throw notZip("centrální adresář mimo soubor");
  const view = await readRange(blob, dir.offset, dir.offset + dir.size);
  const decoder = new TextDecoder("utf-8");
  const entries: ZipEntry[] = [];
  let at = 0;
  for (let i = 0; i < dir.count; i++) {
    if (at + 46 > view.byteLength || view.getUint32(at, true) !== SIG_CENTRAL) {
      throw notZip("poškozený záznam centrálního adresáře");
    }
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const end = at + 46 + nameLength + extraLength + commentLength;
    if (end > view.byteLength) throw notZip("záznam centrálního adresáře přesahuje adresář");
    const entry: ZipEntry = {
      // Takeout píše jména v UTF-8, ale ne každý zapisovač nastaví bit 11.
      name: decoder.decode(new Uint8Array(view.buffer, view.byteOffset + at + 46, nameLength)),
      flags: view.getUint16(at + 8, true),
      method: view.getUint16(at + 10, true),
      compressedSize: view.getUint32(at + 20, true),
      uncompressedSize: view.getUint32(at + 24, true),
      localHeaderOffset: view.getUint32(at + 42, true),
    };
    applyZip64Extra(view, at + 46 + nameLength, extraLength, entry);
    if (!entry.name.endsWith("/")) entries.push(entry);
    at = end;
  }
  return entries;
}

/** Nekomprimovaný obsah jedné položky. */
export async function readZipEntry(blob: Blob, entry: ZipEntry): Promise<Uint8Array<ArrayBuffer>> {
  if (entry.flags & 0x0001) throw unsupported(`šifrovaná položka ${entry.name}`);
  if (entry.method !== 0 && entry.method !== 8)
    throw unsupported(`metoda ${entry.method} u ${entry.name}`);
  // Komprimovaná data se čtou celá do paměti: strop i pro ně (deflate u nestlačitelných dat
  // přidá 5 B na blok 64 KiB, rezerva 1 % to pokryje s velkým přebytkem).
  if (
    entry.uncompressedSize > TAKEOUT_MAX_ENTRY_BYTES ||
    entry.compressedSize > TAKEOUT_MAX_ENTRY_BYTES * 1.01
  )
    throw unsupported(`položka ${entry.name} je příliš velká`);

  const local = await readRange(blob, entry.localHeaderOffset, entry.localHeaderOffset + 30);
  if (local.getUint32(0, true) !== SIG_LOCAL) throw notZip(`chybí lokální hlavička ${entry.name}`);
  // Délka jména a extra pole z LOKÁLNÍ hlavičky (extra pole se od centrálního liší),
  // velikost dat z CENTRÁLNÍHO adresáře (lokální je při bitu 3 nulová, u ZIP64 0xFFFFFFFF).
  const dataStart =
    entry.localHeaderOffset + 30 + local.getUint16(26, true) + local.getUint16(28, true);
  const dataEnd = dataStart + entry.compressedSize;
  if (dataEnd > blob.size) throw notZip(`data ${entry.name} mimo soubor`);
  // Chyby I/O souboru (přesunutý či smazaný soubor) propadnou nepřeložené u obou metod:
  // čtení je mimo `try`, ten obaluje jen dekompresi bajtů už v paměti.
  const raw = new Uint8Array(await blob.slice(dataStart, dataEnd).arrayBuffer());

  let bytes: Uint8Array<ArrayBuffer>;
  if (entry.method === 0) {
    bytes = raw;
  } else {
    bytes = await inflateBounded(raw, entry);
  }
  if (bytes.length !== entry.uncompressedSize) throw unsupported(`délka ${entry.name} nesedí`);
  return bytes;
}

/**
 * Deflate položky po částech se stropem na deklarovanou `uncompressedSize`: položka, která se
 * nafoukne víc, než deklaruje (zip bomba), se přeruší hned po překročení, ne až po dekompresi
 * celého výstupu do paměti.
 */
async function inflateBounded(
  raw: Uint8Array<ArrayBuffer>,
  entry: ZipEntry,
): Promise<Uint8Array<ArrayBuffer>> {
  const out = new Uint8Array(entry.uncompressedSize);
  let length = 0;
  let overflow = false;
  try {
    const reader = new Blob([raw])
      .stream()
      .pipeThrough(new DecompressionStream("deflate-raw"))
      .getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (length + value.length > out.length) {
        overflow = true;
        await reader.cancel().catch(() => {});
        break;
      }
      out.set(value, length);
      length += value.length;
    }
  } catch {
    throw unsupported(`poškozená komprese ${entry.name}`);
  }
  if (overflow || length !== out.length) throw unsupported(`délka ${entry.name} nesedí`);
  return out;
}
